import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Bike, Flag, Fish, Bird, Wallet, Leaf, Sun, Waves, CheckCircle2 } from 'lucide-react';
import SEO from '../components/SEO';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MoyenneSaison = () => {
  const [pricing, setPricing] = useState([]);

  useEffect(() => {
    const loadPricing = async () => {
      try {
        const res = await axios.get(`${API}/pricing`);
        if (Array.isArray(res.data)) setPricing(res.data);
      } catch (e) {
        console.log('Tarifs non chargés');
      }
    };
    loadPricing();
  }, []);

  const isMidSeason = (period = '') => {
    const p = period.toLowerCase();
    return p.includes('avril') || p.includes('mai') || p.includes('septembre') || p.includes('octobre');
  };

  const faqs = [
    {
      q: "Quand a lieu la moyenne saison à l'Île de Ré ?",
      a: "La moyenne saison couvre les mois d'avril, mai, septembre et octobre. C'est la période idéale pour profiter de l'Île de Ré au calme, avec une lumière exceptionnelle, des tarifs bien plus attractifs qu'en juillet-août et une nature préservée."
    },
    {
      q: "Peut-on louer des vélos à l'Île de Ré en avril, mai, septembre ou octobre ?",
      a: "La plupart des loueurs de vélos des Portes-en-Ré sont fermés en moyenne saison. C'est pourquoi la Villa Nomade met gratuitement des vélos à disposition de ses hôtes, afin de parcourir en toute liberté les pistes cyclables, les marais salants et les villages de l'île."
    },
    {
      q: "Le golf de Trousse-Chemise est-il ouvert en moyenne saison ?",
      a: "Oui, le golf de Trousse-Chemise (parcours links 9 trous) reste ouvert en avril, mai, septembre et octobre, avec des green fees plus doux qu'en haute saison. Il est accessible à pied depuis le jardin de la villa."
    },
    {
      q: "Quelles activités faire à l'Île de Ré hors saison ?",
      a: "La moyenne saison est parfaite pour la pêche à pied, l'observation ornithologique dans la Réserve naturelle de Lilleau des Niges, le golf, les balades à vélo dans les marais et la découverte authentique des villages et des cabanes ostréicoles."
    },
    {
      q: "La villa est-elle adaptée aux séminaires d'entreprise hors saison ?",
      a: "Absolument. La grande table de la pièce de vie permet de réunir et faire travailler une équipe, la connexion Wifi performante (150 Mbit/s) et le cadre calme en font un lieu idéal pour les petits séminaires de direction en moyenne saison."
    }
  ];

  return (
    <>
      <SEO
        title="Vacances Moyenne Saison Île de Ré | Avril, Mai, Septembre, Octobre - Villa Nomade"
        description="Séjournez à l'Île de Ré en moyenne saison (avril, mai, septembre, octobre) : tarifs très attractifs, vélos mis à disposition, golf ouvert, pêche, visites ornithologiques et cadre authentique. La Villa Nomade vous accueille au calme aux Portes-en-Ré."
        keywords="moyenne saison Île de Ré, vacances avril mai Île de Ré, vacances septembre octobre Île de Ré, hors saison Île de Ré, location villa hors saison Ré, vélos Île de Ré, ornithologie Île de Ré, pêche à pied Ré, tarifs attractifs Île de Ré"
        image="https://images.unsplash.com/photo-1752606302768-8953e76056dd?w=1200"
        url="/moyenne-saison-ile-de-re"
      />

      {/* Hero */}
      <div className="relative h-[60vh] min-h-[400px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1752606302768-8953e76056dd?w=1600)' }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/70"></div>
        </div>
        <div className="relative h-full flex items-center justify-center text-white px-4">
          <div className="text-center max-w-4xl">
            <Leaf className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
              L'Île de Ré en Moyenne Saison
            </h1>
            <p className="text-lg sm:text-xl text-gray-200">
              Avril • Mai • Septembre • Octobre — l'île authentique, au calme et à prix doux
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Introduction */}
        <section className="mb-16 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">La Meilleure Période pour Découvrir l'Île de Ré</h2>
          <p className="text-lg text-gray-700 leading-relaxed max-w-4xl mx-auto">
            En <strong>avril, mai, septembre et octobre</strong>, l'Île de Ré se dévoile sous son plus beau jour :
            lumière dorée, pistes cyclables tranquilles, plages désertes et villages retrouvant leur âme.
            À la <strong>Villa Nomade</strong>, aux Portes-en-Ré, vous profitez d'un cadre préservé, de
            <strong> tarifs très attractifs</strong> et d'une multitude d'activités encore accessibles, loin de l'affluence estivale.
          </p>
        </section>

        {/* Avantages */}
        <section className="mb-16">
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 text-center">
              <Wallet className="w-10 h-10 text-amber-600 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Tarifs très attractifs</h3>
              <p className="text-sm text-gray-600">Jusqu'à plusieurs centaines d'euros d'économie par semaine vs haute saison</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 text-center">
              <Bike className="w-10 h-10 text-amber-600 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Vélos à disposition</h3>
              <p className="text-sm text-gray-600">Les loueurs sont fermés : nous vous prêtons nos vélos gratuitement</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 text-center">
              <Flag className="w-10 h-10 text-amber-600 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Golf ouvert</h3>
              <p className="text-sm text-gray-600">Trousse-Chemise reste ouvert, à pied depuis le jardin</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 text-center">
              <Bird className="w-10 h-10 text-amber-600 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Nature préservée</h3>
              <p className="text-sm text-gray-600">Ornithologie, marais salants et plages sauvages au calme</p>
            </div>
          </div>
        </section>

        {/* Vélos */}
        <section className="mb-16">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <img
              src="https://images.unsplash.com/photo-1563310196-ea35538af4e1?w=900"
              alt="Balade à vélo sur les pistes cyclables de l'Île de Ré en moyenne saison"
              className="w-full h-[350px] object-cover rounded-lg shadow-lg"
            />
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Bike className="w-8 h-8 text-amber-700" />
                <h2 className="text-2xl font-bold text-gray-900">Vélos Mis à Disposition</h2>
              </div>
              <p className="text-gray-700 mb-4">
                En moyenne saison, la plupart des loueurs de vélos des Portes-en-Ré sont <strong>fermés</strong>.
                Pas de souci : la Villa Nomade met <strong>gratuitement ses vélos à votre disposition</strong> pour
                explorer l'île en toute liberté.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> Plus de 100 km de pistes cyclables sécurisées</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> Traversée des marais salants et de la réserve naturelle</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> Accès direct aux plages de la Patache et de Trousse-Chemise</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> Villages d'Ars-en-Ré et Saint-Clément à portée de pédale</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Golf + Pêche */}
        <section className="mb-16 grid md:grid-cols-2 gap-8">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-lg">
            <div className="flex items-center gap-3 mb-4">
              <Flag className="w-8 h-8 text-green-700" />
              <h2 className="text-2xl font-bold text-gray-900">Golf Ouvert Hors Saison</h2>
            </div>
            <p className="text-gray-700 mb-4">
              Le <strong>Golf de Trousse-Chemise</strong> (parcours links 9 trous) reste ouvert en avril, mai,
              septembre et octobre — souvent avec des <strong>green fees plus doux</strong>. Un luxe rare :
              le parcours est accessible <strong>à pied depuis le jardin de la villa</strong>.
            </p>
            <Link to="/golf-ile-de-re" className="text-green-700 font-medium hover:underline">En savoir plus sur le golf →</Link>
          </div>
          <div className="bg-gradient-to-br from-sky-50 to-blue-50 p-8 rounded-lg">
            <div className="flex items-center gap-3 mb-4">
              <Fish className="w-8 h-8 text-blue-700" />
              <h2 className="text-2xl font-bold text-gray-900">Pêche & Authenticité</h2>
            </div>
            <p className="text-gray-700 mb-4">
              La moyenne saison est le paradis des amateurs de <strong>pêche à pied</strong> et d'authenticité :
              grandes marées, coques, palourdes et huîtres des cabanes ostréicoles voisines. Retrouvez l'île
              telle que les Rétais l'aiment, hors de l'agitation estivale.
            </p>
            <img
              src="https://images.unsplash.com/photo-1752606302855-2df5cf459224?w=800"
              alt="Cabanes ostréicoles et pêche à pied à marée basse sur l'Île de Ré"
              className="w-full h-40 object-cover rounded-lg mt-2"
            />
          </div>
        </section>

        {/* Ornithologie */}
        <section className="mb-16">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="order-2 md:order-1">
              <div className="flex items-center gap-3 mb-4">
                <Bird className="w-8 h-8 text-amber-700" />
                <h2 className="text-2xl font-bold text-gray-900">Visites Ornithologiques</h2>
              </div>
              <p className="text-gray-700 mb-4">
                À quelques minutes de la villa, la <strong>Réserve naturelle de Lilleau des Niges</strong> abrite
                des centaines d'espèces d'oiseaux. Le printemps et l'automne sont les <strong>meilleures périodes
                d'observation</strong> : hérons, aigrettes, avocettes, barges et oiseaux migrateurs peuplent les marais.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> Réserve naturelle de Lilleau des Niges (Portes-en-Ré)</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> Sorties guidées de la LPO au fil des saisons</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> Observation des migrateurs au printemps et à l'automne</li>
              </ul>
            </div>
            <img
              src="https://images.unsplash.com/photo-1719742734364-1f25d6a5fb79?w=900"
              alt="Aigrette blanche dans les marais - observation ornithologique à l'Île de Ré"
              className="order-1 md:order-2 w-full h-[350px] object-cover rounded-lg shadow-lg"
            />
          </div>
        </section>

        {/* Tarifs dynamiques */}
        <section className="mb-16 bg-gray-50 p-8 rounded-lg">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">Des Tarifs Très Attractifs</h2>
          <p className="text-center text-gray-600 mb-8">Profitez de la Villa Nomade à prix doux en avril, mai, septembre et octobre</p>
          {pricing.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {pricing.map((p) => (
                <div
                  key={p.id}
                  className={`p-6 rounded-lg text-center border ${isMidSeason(p.period) ? 'bg-amber-50 border-amber-300 shadow-md' : 'bg-white border-gray-200'}`}
                >
                  <div className="text-sm text-gray-600 mb-1">{p.period}</div>
                  <div className={`text-2xl font-bold mb-1 ${isMidSeason(p.period) ? 'text-amber-700' : 'text-gray-900'}`}>{p.price}</div>
                  <div className="text-xs text-gray-500">{p.description}</div>
                  {isMidSeason(p.period) && (
                    <div className="mt-3 inline-block text-xs font-semibold text-amber-800 bg-amber-100 px-3 py-1 rounded-full">Moyenne saison</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-600">Contactez-nous pour connaître nos tarifs moyenne saison.</p>
          )}
          <p className="text-center text-gray-600 mt-6 text-sm">Tarifs indicatifs par semaine, hors charges éventuelles. Consultez la page réservation pour les disponibilités.</p>
        </section>

        {/* Séminaires teaser */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-stone-100 to-amber-50 p-8 rounded-lg grid md:grid-cols-2 gap-6 items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Idéale aussi pour vos Séminaires</h2>
              <p className="text-gray-700 mb-4">
                La moyenne saison est parfaite pour réunir votre équipe : grande table de travail, Wifi performant
                (150 Mbit/s), cadre calme et inspirant. La villa se transforme en lieu de séminaire de direction privilégié.
              </p>
              <Link to="/seminaire-ile-de-re">
                <Button className="bg-amber-700 hover:bg-amber-800 text-white">Découvrir l'offre séminaires</Button>
              </Link>
            </div>
            <img
              src="https://images.unsplash.com/photo-1633457896836-f8d6025c85d1?w=900"
              alt="Réunion d'équipe autour de la grande table de la villa - séminaire à l'Île de Ré"
              className="w-full h-56 object-cover rounded-lg shadow-md"
            />
          </div>
        </section>

        {/* FAQ (AEO) */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Questions Fréquentes</h2>
          <div className="space-y-4 max-w-4xl mx-auto">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                <h3 className="font-semibold text-lg text-gray-900 mb-2">{f.q}</h3>
                <p className="text-gray-700">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-[#c17a3a] to-[#a66830] text-white p-12 rounded-lg text-center">
          <Sun className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">Réservez votre Séjour en Moyenne Saison</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Vélos offerts • Golf à pied • Nature préservée • Tarifs doux
            <br />L'Île de Ré authentique n'attend que vous
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/reservation">
              <Button size="lg" className="bg-white text-[#c17a3a] hover:bg-gray-100">Voir les disponibilités</Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">Nous contacter</Button>
            </Link>
          </div>
        </section>
      </div>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}
      </script>
    </>
  );
};

export default MoyenneSaison;
